/**
 * The stage's actions (docs/features/badger-cohost.md, Principles 1 and 2): show an athlete, show a leaderboard, show an
 * athlete on the host's map, and clear. One executor, two ways in: the host console's buttons call it, and Badger will
 * call the same functions over LiveKit RPC (phase 3), so it knows nothing of the UI. Each action checks what the page
 * can check, runs as the live host (the server still owns every write and validates every spec), and answers what it
 * showed or why not, in words Badger can say.
 */

import { useMemo, useRef } from 'react'
import { apiFailure } from '@/lib/apiFailure'
import { waitWords } from '@/lib/retryWait'
import { watchApi } from './api'
import type { EventAthlete } from './eventAthletes'
import {
  GRAPHIC_SLOTS,
  MAX_BOARD_ATHLETES,
  PANEL_LIMITS,
  TITLE_LENGTH,
  type GraphicSlot,
  type RaceGender,
  type StageGraphicInput,
} from './graphics'
import { raceById, standingsTitle, type Race } from './races'
import { mapSource } from './rules'

export type StageActionResult = { ok: true; shown: string } | { ok: false; reason: string }

/** What `showStandings` asks for: a race, optionally a gender or a hand-picked few, how many rows and a title. */
export interface ShowStandingsInput {
  routeId: string
  gender?: RaceGender | null
  athleteIds?: readonly string[] | null
  limit?: number | null
  title?: string | null
}

export interface StageActions {
  /** The athlete's card in the lower third, for `holdS` seconds (the API's 30 by default). */
  showAthlete(athleteId: string, options?: { holdS?: number }): Promise<StageActionResult>
  /** A leaderboard in the panel, until cleared or replaced. */
  showStandings(input: ShowStandingsInput): Promise<StageActionResult>
  /** The host's own map called for their audience, the play world on their canvas, flying to the athlete. */
  showOnMap(athleteId: string): Promise<StageActionResult>
  /** Clears one slot, or both without one. */
  clearGraphic(slot?: GraphicSlot): Promise<StageActionResult>
}

type StageActionsApi = Pick<typeof watchApi, 'showGraphic' | 'clearGraphic' | 'athleteProfile'>

export interface StageActionContext {
  eventId: string
  stageId: string
  /** This tab's host is live on the stage: only then do actions run. */
  live: boolean
  /** The event's public roster as the page holds it; empty before it loads (the server checks then). */
  roster: readonly EventAthlete[]
  races: readonly Race[]
  /** Why Show on map can't run on this event (it has no course map), or null when it can. */
  mapUnavailable: string | null
  /** This tab's LiveKit identity: its map is `<identity>:map`. */
  hostIdentity: string | null
  /** Calls a source for the stage's audience (`useWatchScreen`'s `callCamera`). */
  callCamera(identity: string | null): Promise<unknown>
  /** Puts the map on the host's canvas (the play world where it has one) and flies to the athlete with their card open. */
  focusAthleteOnMap?(athleteId: string): void
  /** The API, for tests. */
  api?: StageActionsApi
}

export const NOT_LIVE = 'Go live on this stage first'
export const NO_ATHLETE = 'That athlete isn’t on the public roster'
export const NO_RACE = 'That race isn’t one of this event’s'
export const NOT_ON_MAP = 'They’re not on the map yet'
export const FAILED = 'That didn’t go through. Try again.'

/** Why the server refused, in words: its own for a refused spec, ours for the rest. Badger's actions share it. */
export function refusal(failure: unknown): string {
  const { status, message, retryAfterMs } = apiFailure(failure)
  if (status === 429) return `Too many changes. Try again in ${waitWords(retryAfterMs) ?? 'a moment'}.`
  if (status === 403) return 'Only a live host on this stage can do that'
  if (status === 422 && message) return message
  return FAILED
}

const fail = (reason: string): StageActionResult => ({ ok: false, reason })

/**
 * The stage's actions over a context read at each call (`context()`), so they stay the same functions while the roster,
 * the races or the live state change.
 */
export function createStageActions(context: () => StageActionContext): StageActions {
  const apiOf = (ctx: StageActionContext) => ctx.api ?? watchApi

  /** The athlete, when the page can find them (or can't tell yet: an empty roster leaves it to the server). */
  const athleteOf = (ctx: StageActionContext, athleteId: string): EventAthlete | 'unknown' | null => {
    if (ctx.roster.length === 0) return 'unknown'
    return ctx.roster.find((athlete) => athlete.id === athleteId) ?? null
  }

  const show = async (ctx: StageActionContext, slot: GraphicSlot, graphic: StageGraphicInput, shown: string): Promise<StageActionResult> => {
    try {
      await apiOf(ctx).showGraphic(ctx.stageId, slot, graphic)
      return { ok: true, shown }
    } catch (failure) {
      return fail(refusal(failure))
    }
  }

  return {
    async showAthlete(athleteId, options) {
      const ctx = context()
      if (!ctx.live) return fail(NOT_LIVE)
      const athlete = athleteOf(ctx, athleteId)
      if (!athlete) return fail(NO_ATHLETE)
      const holdS = options?.holdS
      return show(
        ctx,
        'lower_third',
        { kind: 'athlete', athleteId, ...(holdS ? { holdS: Math.round(holdS) } : {}) },
        athlete === 'unknown' ? 'the athlete' : athlete.name,
      )
    },

    async showStandings({ routeId, gender = null, athleteIds = null, limit = null, title = null }) {
      const ctx = context()
      if (!ctx.live) return fail(NOT_LIVE)
      const race = raceById(ctx.races, routeId)
      if (!race) return fail(NO_RACE)
      const picked = athleteIds ? Array.from(new Set(athleteIds)).slice(0, MAX_BOARD_ATHLETES) : []
      if (picked.some((id) => !athleteOf(ctx, id))) return fail(NO_ATHLETE)
      const own = title?.trim().slice(0, TITLE_LENGTH) || null
      const rows = typeof limit === 'number' && Number.isFinite(limit) ? Math.min(Math.max(Math.round(limit), PANEL_LIMITS.min), PANEL_LIMITS.max) : null
      return show(
        ctx,
        'panel',
        { kind: 'standings', routeId, gender, limit: rows, athleteIds: picked.length ? picked : null, title: own },
        standingsTitle(race, gender, own),
      )
    },

    async showOnMap(athleteId) {
      const ctx = context()
      if (!ctx.live || !ctx.hostIdentity) return fail(NOT_LIVE)
      if (ctx.mapUnavailable) return fail(ctx.mapUnavailable)
      const athlete = athleteOf(ctx, athleteId)
      if (!athlete) return fail(NO_ATHLETE)
      // Only an athlete with a fix has a dot to fly to.
      try {
        const { data } = await apiOf(ctx).athleteProfile(ctx.eventId, athleteId)
        if (!data) return fail(NO_ATHLETE)
        if (!data.position) return fail(NOT_ON_MAP)
      } catch (failure) {
        const status = (failure as { status?: unknown } | null)?.status
        return fail(status === 404 ? NO_ATHLETE : FAILED)
      }
      // The host's own world first, so they see it move at once; then their audience follows it.
      ctx.focusAthleteOnMap?.(athleteId)
      try {
        await ctx.callCamera(mapSource(ctx.hostIdentity))
      } catch (failure) {
        return fail(refusal(failure))
      }
      return { ok: true, shown: athlete === 'unknown' ? 'the athlete on the map' : `${athlete.name} on the map` }
    },

    async clearGraphic(slot) {
      const ctx = context()
      if (!ctx.live) return fail(NOT_LIVE)
      const slots = slot ? [slot] : GRAPHIC_SLOTS
      try {
        await Promise.all(slots.map((each) => apiOf(ctx).clearGraphic(ctx.stageId, each)))
        return { ok: true, shown: 'Cleared' }
      } catch (failure) {
        return fail(refusal(failure))
      }
    },
  }
}

/** The stage's actions for the page: the same functions for the page's life, reading the newest context at each call. */
export function useStageActions(context: StageActionContext): StageActions {
  const latest = useRef(context)
  latest.current = context
  return useMemo(() => createStageActions(() => latest.current), [])
}
